﻿<script setup lang="ts">
    import { onMounted, reactive } from 'vue';
    import { useRouter } from 'vue-router';
    import {
        getAccount,
        Account,
        saveAccount,
        getAccountUsers,
        User,
        getSmtpServers,
        SmtpServer,
        getAccountStatusClass,
        getAccountEmailsPendingValidation,
        EmailValidation,
        validateEmail,
        getContactDeleteCount,
        deleteContactsByType,
        getClientCompanies,
        validateAccountDomain,
        getStoredDomainValidation,
        type DomainValidationStatus,
    } from '@cwd/Views/BackOffice/Account/Account.access';
    import Tabs, { tab } from '@shared/components/Tabs.vue';
    import Loading from '@shared/components/Loading.vue';
    import CopyButton from '@shared/components/CopyButton.vue';
    import Form from '@cwd/shared/Components/Form/Form.vue';
    import { FormProps } from '@cwd/shared/components/Form/Form';
    import DomainWrapper from './DomainWrapper.vue';
    import { formatDate } from '@cwd/Shared/utils/formatDateTime';
    import { vConfirm } from '@shared/directives/VConfirm';
    import { bsAlert, AlertTypes } from '@shared/components/BsAlerts';
    import BsAlert from '@shared/components/BsAlert.vue';
    import List from '@shared/components/List/List.vue';
    import { ClientCompany } from '@shared/Root/Context.access';
    import { ClientCompanyReference } from '@shared/references/ClientCompanyReference';
    import DeleteAccount from './DeleteAccount.vue';
    import AccountDataSources from './AccountDataSources.vue';
    import AccountJobs from './AccountJobs.vue';
    import DomainValidation from './DomainValidation.vue';
    import DisabledFeatures from './DisabledFeatures.vue';
    import FormRadioGroup from '@shared/components/Form/FormRadioGroup.vue';

    const router = useRouter();

    const props = defineProps<{
        accountId: string;
    }>();

    const backRouteLabels: Record<string, string> = {
        AccountList: 'Accounts',
        SubscriptionEdit: 'Subscription',
        UserEdit: 'User',
        SmtpServerDetail: 'SMTP Server',
    };

    const backRouteName = window.history.state?.back ? router.resolve(window.history.state.back).name?.toString() : undefined;
    const backLabel = (backRouteName && backRouteLabels[backRouteName]) || 'Accounts';

    function goBack(): void {
        if (window.history.state?.back) router.back();
        else router.push({ name: 'AccountList' });
    }

    const views = {
        currentAccount: 0,
        companiesList: 1,
    };

    const state = reactive<{
        account: Account | null;
        companies: ClientCompanyReference[] | null;
        currentView: number;
        isLoading: boolean;
        isValidating: boolean;
        isLoadingContacts: boolean;
        formReady: boolean;
        settingsForm: FormProps;
        senderSettingsForm: FormProps;
        smtpServers: SmtpServer[];
        users: User[];
        emailValidations: EmailValidation[];
        contactDeleteType: string;
        contactDeleteCount: number | null;
        retention: number;
        domainValidation: DomainValidationStatus | null;
        isDomainValidating: boolean;
    }>({
        account: null,
        companies: null,
        currentView: views.currentAccount,
        isLoading: false,
        isValidating: false,
        isLoadingContacts: false,
        formReady: false,
        settingsForm: {
            disableSubmit: false,
            alert: null,
            data: {},
        },
        senderSettingsForm: {
            disableSubmit: false,
            alert: null,
            data: {},
        },
        smtpServers: [],
        users: [],
        emailValidations: [],
        contactDeleteType: 'deleted',
        contactDeleteCount: null,
        retention: 180,
        domainValidation: null,
        isDomainValidating: false,
    });

    onMounted(async () => {
        await get();
    });

    async function get(): Promise<void> {
        state.isLoading = true;

        await Promise.all([getAccountData(), getUsers(), getSmtp()]);

        setupForms();

        state.isLoading = false;

        await getEmailsPendingValidation();

        getStoredDomainValidation(props.accountId).then((v) => {
            state.domainValidation = v;
        });
    }

    async function getAccountData(): Promise<void> {
        await getAccount(props.accountId)
            .then((data) => {
                state.account = data;
            })
            .catch(() => {
                bsAlert({ title: 'Error', content: 'Failed to get account', type: AlertTypes.Danger });
            });
    }

    async function getClientCompaniesData(): Promise<void> {
        if (state.isLoading) return;
        state.isLoading = true;

        await getClientCompanies().then((data) => {
            state.companies = data;
        });

        state.isLoading = false;
    }

    async function getUsers(): Promise<void> {
        await getAccountUsers(props.accountId).then((data) => {
            state.users = data;
        });
    }

    async function getSmtp(): Promise<void> {
        await getSmtpServers().then((data) => {
            state.smtpServers = data;
        });
    }

    async function getEmailsPendingValidation(): Promise<void> {
        await getAccountEmailsPendingValidation(props.accountId).then((data) => {
            state.emailValidations = data;
        });
    }

    async function validate(validation: EmailValidation): Promise<void> {
        if (state.isValidating === true) return;

        state.isValidating = true;

        await validateEmail(validation)
            .then((data) => {
                validation.IsValidated = true;
            })
            .catch(() => {})
            .finally(() => (state.isValidating = false));
    }

    async function saveSettings(event: Event, formData: any) {
        if (state.isLoading === true || !state.account) return;

        if (hasInvalidTimeLimitedPeriod()) {
            bsAlert({
                type: AlertTypes.Danger,
                title: 'Validation Error',
                content: 'A time-limited account needs a start date and an end date after it.',
            });
            return;
        }

        state.isLoading = true;
        state.settingsForm.setInitData = false;

        // A copy: a failed save must not leave unsaved edits (e.g. IsTimeLimited off) for another tab's save to post.
        await saveAccount({ ...state.account, ...formData })
            .then((data) => {
                state.account = data;
                state.settingsForm.setInitData = true;
                // Turned ordinary: the option is gone for good (it is only chosen at creation).
                if (data.IsTimeLimited !== true && state.settingsForm.data.IsTimeLimited) state.settingsForm.data.IsTimeLimited.show = false;

                bsAlert({ title: 'Saved', content: 'Account saved' });
            })
            .catch(() => {
                bsAlert({ title: 'Error', content: 'Something went wrong', type: AlertTypes.Danger });
            })
            .finally(() => (state.isLoading = false));
    }

    async function saveSenderSettings(event: Event, formData: any) {
        if (state.isLoading === true || !state.account) return;

        state.isLoading = true;
        state.senderSettingsForm.setInitData = false;

        await saveAccount({ ...state.account, ...formData })
            .then((data) => {
                state.account = data;
                state.senderSettingsForm.setInitData = true;

                bsAlert({ title: 'Saved', content: 'Account saved' });

                if (data.DefaultFromAddress) checkDomainValidation(data.DefaultFromAddress);
            })
            .catch(() => {
                bsAlert({ title: 'Error', content: 'Something went wrong', type: AlertTypes.Danger });
            })
            .finally(() => (state.isLoading = false));
    }

    async function checkDomainValidation(email: string): Promise<void> {
        if (!email) return;
        state.isDomainValidating = true;
        try {
            const result = await validateAccountDomain(email);
            state.domainValidation = { ...result, ValidatedAt: result.ValidationDate };
        } catch {
            // silent — don't interrupt workflow
        } finally {
            state.isDomainValidating = false;
        }
    }

    function handleSenderSettingsBlur(key: string): void {
        if (key === 'DefaultFromAddress') {
            const email = state.senderSettingsForm.data.DefaultFromAddress?.modelValue as string;
            if (email) checkDomainValidation(email);
        }
    }

    function onContactDeleteTypeChange(value: string) {
        state.contactDeleteType = value;
        state.contactDeleteCount = null;
    }

    async function countDeletion() {
        if (state.isLoadingContacts === true) return;

        state.contactDeleteCount = null;
        state.isLoadingContacts = true;

        await getContactDeleteCount(props.accountId, state.contactDeleteType)
            .then((data) => {
                state.contactDeleteCount = data;
            })
            .catch(() => {})
            .finally(() => (state.isLoadingContacts = false));
    }

    async function deleteContacts() {
        if (state.isLoadingContacts === true) return;

        state.isLoadingContacts = true;

        await deleteContactsByType(props.accountId, state.contactDeleteType)
            .then((data) => {
                state.contactDeleteCount = null;
            })
            .catch(() => {})
            .finally(() => (state.isLoadingContacts = false));
    }

    function onSettingsChange(key: string, newVal: any): void {
        state.settingsForm.data[key].modelValue = newVal;

        // Chargebee F2b: unticking turns the account ordinary — it goes on without an end (the server clears End too).
        if (key === 'IsTimeLimited' && newVal === false) {
            state.settingsForm.data.End.modelValue = null;
            state.settingsForm.data.Start.required = false;
            state.settingsForm.data.End.required = false;
        }

        // Re-ticked before saving: back to the loaded End, both dates required again.
        if (key === 'IsTimeLimited' && newVal === true) {
            state.settingsForm.data.End.modelValue = state.account?.End ?? null;
            state.settingsForm.data.Start.required = true;
            state.settingsForm.data.End.required = true;
        }
    }

    // A time-limited account needs a Start and an End after it (CK_Account_IsTimeLimited).
    function hasInvalidTimeLimitedPeriod(): boolean {
        const data = state.settingsForm.data;
        if (data.IsTimeLimited?.modelValue !== true) return false;

        // Date parts as strings: a loaded "…T00:00:00" parses as local time, a typed "yyyy-MM-dd" as UTC.
        const start = String(data.Start.modelValue ?? '').substring(0, 10);
        const end = String(data.End.modelValue ?? '').substring(0, 10);
        return !start || !end || end <= start;
    }

    function setupForms(): void {
        setupSettingsFormData();
        setupSenderSettingsFormData();

        state.formReady = true;
    }

    function setupSettingsFormData(): void {
        state.settingsForm.data = {
            Name: {
                modelValue: state.account?.Name,
                group: 'Account',
                required: true,
                input: {
                    label: 'Name',
                },
            },
            Status: {
                modelValue: state.account?.Status,
                group: 'Account',
                select: {
                    label: 'Status',
                    options: [
                        { value: '2', label: 'Active' },
                        { value: '3', label: 'Cancelled' },
                        { value: '4', label: 'Pending Validate' },
                    ],
                    readonly: true,
                },
            },
            // Only on a time-limited account (chosen at creation only): untick to make it ordinary. Not in the form otherwise, so
            // a save never posts false for an account it did not load as time-limited — false turns one ordinary.
            ...(state.account?.IsTimeLimited === true
                ? {
                      IsTimeLimited: {
                          modelValue: true,
                          group: 'Active period',
                          checkbox: {
                              label: 'Time-limited account',
                              description:
                                  'Billed once for Start–End. Untick to keep it as an ordinary account (End is cleared); billed as one once its paid period has run out.',
                          },
                      },
                  }
                : {}),
            Start: {
                modelValue: state.account?.Start,
                group: 'Active period',
                required: state.account?.IsTimeLimited === true,
                input: {
                    label: 'Start',
                    type: 'date',
                },
            },
            End: {
                modelValue: state.account?.End,
                group: 'Active period',
                required: state.account?.IsTimeLimited === true,
                input: {
                    label: 'End',
                    type: 'date',
                },
            },
            EnabledAuthenticationMethods: {
                modelValue: state.account?.EnabledAuthenticationMethods,
                group: 'Auth',
                select: {
                    label: 'Enabled authentication methods',
                    options: [
                        { value: '1', label: 'Password' },
                        { value: '2', label: 'BankId' },
                        { value: '4', label: 'PasswordAndGoogleAuthenticator' },
                        { value: '8', label: 'SSO (OpenId)' },
                    ],
                    readonly: true,
                },
            },
            DefaultAccessControl: {
                modelValue: state.account?.DefaultAccessControl,
                group: 'Auth',
                select: {
                    label: 'ACL default',
                    options: [
                        { value: '0', label: 'None' },
                        { value: '1', label: 'CreatingUser' },
                        { value: '3', label: 'CreatingUserGroups' },
                    ],
                    readonly: true,
                },
            },
            ContactPerson: {
                group: 'Account',
                modelValue: state.account?.ContactPerson,
                input: {
                    label: 'Contact person',
                },
            },
            Comments: {
                group: 'Other',
                modelValue: state.account?.Comments,
                textArea: {
                    label: 'Comments',
                },
            },
        };
    }

    function setupSenderSettingsFormData(): void {
        let smtpServerOptions = state.smtpServers.map((server) => {
            return { value: server.SmtpServerId, label: server.Name };
        });

        state.senderSettingsForm.data = {
            SmtpServerId: {
                modelValue: state.account?.SmtpServerId,
                group: 'SMTP',
                required: true,
                select: {
                    label: 'Delivery smtp server',
                    options: smtpServerOptions,
                    readonly: true,
                },
            },
            SystemSmtpServerId: {
                modelValue: state.account?.SystemSmtpServerId,
                group: 'SMTP',
                select: {
                    label: 'Transactional smtp server',
                    options: smtpServerOptions,
                    readonly: true,
                },
            },
            UseSmtpServerDkimOverrides: {
                modelValue: state.account?.UseSmtpServerDkimOverrides,
                required: false,
                checkbox: {
                    label: 'Use SmtpServer Dkim Overrides',
                },
            },
            SendWithVerp: {
                modelValue: state.account?.SendWithVerp,
                required: false,
                checkbox: {
                    label: 'Send with VERP (Variable envelope return path)',
                },
            },
            DefaultReplyToAddress: {
                modelValue: state.account?.DefaultReplyToAddress,
                required: true,
                input: {
                    label: 'Default Reply-to address (copied to all new emails)',
                },
            },
            DefaultFromName: {
                modelValue: state.account?.DefaultFromName,
                required: true,
                input: {
                    label: 'Default From Name (copied to all new emails)',
                },
            },
            DefaultFromAddress: {
                modelValue: state.account?.DefaultFromAddress,
                required: true,
                input: {
                    label: 'Default From address (bounces go here, used if no specific from address given on an email)',
                },
            },
            SmsAnswerCode: {
                modelValue: state.account?.SmsAnswerCode,
                required: true,
                input: {
                    label: 'Sms answer code',
                },
            },
        };
    }

    const listCompanies = () => {
        state.currentView = views.companiesList;
        !state.companies && getClientCompaniesData();
    };

    async function updateCurrentCompany(data: ClientCompany) {
        if (state.isLoading === true || !state.account) return;
        state.isLoading = true;

        state.account!.ClientCompany.Name = data.Name;
        state.account!.ClientCompany.ClientCompanyId = data.ClientCompanyId;

        await saveAccount(state.account)
            .then((data) => {
                state.account = data;
                bsAlert({ title: 'Saved', content: 'Account saved' });
            })
            .catch(() => {
                bsAlert({ title: 'Error', content: 'Something went wrong', type: AlertTypes.Danger });
            })
            .finally(() => {
                state.isLoading = false;
                state.currentView = views.currentAccount;
            });
    }
</script>

<template>
    <p class="fw-600 m-lg-t m-xs-b">
        <a href="javascript:void(0)" role="button" v-show="state.currentView === views.currentAccount" @click="goBack">
            <span class="glyphicon glyphicon-chevron-left m-xxs-r c-light" aria-hidden="true"></span>
            {{ backLabel }}
        </a>
        <template v-if="backLabel !== 'Accounts'" v-show="state.currentView === views.currentAccount">
            |
            <router-link :to="{ name: 'AccountList' }">All accounts</router-link>
        </template>
        <a href="javascript:void(0)" role="button" v-show="state.currentView === views.companiesList" @click="state.currentView = views.currentAccount">
            <span class="glyphicon glyphicon-chevron-left m-xxs-r c-light" aria-hidden="true"></span>
            {{ state.account?.Name }}
        </a>
    </p>

    <Loading :show="state.isLoading" loadingStyle="progress-bar" :fixed="true"></Loading>

    <template v-if="state.account != null">
        <div v-show="state.currentView === views.currentAccount">
            <div class="m-lg-b">
                <h2 class="h3">
                    <i class="fa fa-square" aria-hidden="true" :class="getAccountStatusClass(state.account)"></i>
                    {{ state.account.Name }}
                    <small class="m-xs-l">
                        <router-link :to="{ name: 'SubscriptionEdit', params: { serviceSubscriptionId: state.account.ServiceSubscriptionId } }">
                            {{ state.account.ClientCompany.Name }}
                        </router-link>
                    </small>
                </h2>

                <a class="btn btn-default btn-sm" :href="'/Account/' + props.accountId" target="_blank" rel="noopener noreferrer">Sign in to this account</a>
                <CopyButton class="m-xs-l" :textToCopy="props.accountId" buttonText="Copy account id" :tooltip="props.accountId"></CopyButton>
            </div>

            <tabs>
                <tab title="Settings">
                    <h3 class="m-lg-b">Settings</h3>

                    <div class="d-flex gap-xs flex-wrap align-items-baseline m-lg-b">
                        Company:
                        <router-link class="strong" :to="{ name: 'SubscriptionEdit', params: { serviceSubscriptionId: state.account.ServiceSubscriptionId } }">
                            {{ state.account.ClientCompany.Name }}
                        </router-link>
                        <button class="btn btn-default btn-sm m-sm-l" type="button" @click="listCompanies">Change</button>
                    </div>

                    <Form
                        v-if="state.formReady"
                        class="fieldset-grid"
                        :data="state.settingsForm.data"
                        :setInitData="state.settingsForm.setInitData"
                        @update:isValid="(key, newVal) => (state.settingsForm!.data[key].isValid = newVal)"
                        @update:modelValue="onSettingsChange"
                        @onSubmit="saveSettings"></Form>
                </tab>

                <tab title="Sender settings">
                    <h3 class="m-lg-b">Sender Settings</h3>

                    <Form
                        v-if="state.formReady"
                        class="fieldset-grid"
                        :data="state.senderSettingsForm.data"
                        :setInitData="state.senderSettingsForm.setInitData"
                        @update:isValid="(key, newVal) => (state.senderSettingsForm!.data[key].isValid = newVal)"
                        @update:modelValue="(key, newVal) => (state.senderSettingsForm.data[key].modelValue = newVal)"
                        @onSubmit="saveSenderSettings"
                        @onBlur="handleSenderSettingsBlur"></Form>

                    <DomainValidation :isValidating="state.isDomainValidating" :validation="state.domainValidation"></DomainValidation>
                </tab>

                <tab title="Domain settings">
                    <h3 class="m-lg-b">Domain Settings</h3>
                    <DomainWrapper :selectedAccount="state.account"></DomainWrapper>
                </tab>

                <tab title="Users">
                    <h3 class="m-lg-b">Users</h3>
                    <List
                        :items="state.users"
                        itemId="UserId"
                        :filterKeys="['Email', 'Name', 'RoleName']"
                        displayAs="table"
                        :tableHeaders="['Email', 'Name', 'Role', 'Created', 'Last login']"
                        :orderOptions="[
                            { key: 'Email', display: 'Email' },
                            { key: 'Name', display: 'Name' },
                            { key: 'RoleName', display: 'Role' },
                            { key: 'Created', display: 'Created' },
                            { key: 'LastLogin', display: 'Last login' },
                        ]"
                        orderByValue="Created"
                        orderByDirection="desc">
                        <template #item="{ item: user }">
                            <td>
                                <i
                                    class="fa fa-square m-sm-r"
                                    aria-hidden="true"
                                    :class="{ 'status-ok': user.Status == 0, 'status-not-ok': user.Status == 1, 'status-warning': user.Status == 2 }"></i>
                                <router-link :to="{ name: 'UserEdit', params: { userId: user.UserId } }">
                                    {{ user.Email == null || user.Email == '' ? 'No email' : user.Email }}
                                </router-link>
                            </td>
                            <td>
                                {{ user.Name }}
                            </td>
                            <td>
                                {{ user.RoleName }}
                            </td>
                            <td>
                                {{ formatDate(user.Created) }}
                            </td>
                            <td>
                                {{ formatDate(user.LastLogin) }}
                            </td>
                        </template>
                    </List>
                </tab>

                <tab title="Validate emails">
                    <h3 class="m-lg-b">Validate emails</h3>
                    <Loading :show="state.isValidating" loadingStyle="progress-bar" :fixed="true"></Loading>

                    <List
                        :items="state.emailValidations"
                        itemId="ValidatedEmailId"
                        :filterKeys="['Email']"
                        displayAs="table"
                        :tableHeaders="['Email', 'Created', '']"
                        :orderOptions="[
                            { key: 'Email', display: 'Email' },
                            { key: 'Created', display: 'Created' },
                        ]"
                        orderByValue="Created"
                        orderByDirection="desc">
                        <template #item="{ item: validation }">
                            <td>{{ validation.Email }}</td>
                            <td>{{ formatDate(validation.Created) }}</td>
                            <td class="text-right">
                                <button
                                    v-if="!validation.IsValidated"
                                    class="btn btn-sm btn-primary"
                                    v-confirm="{ action: () => validate(validation) }"
                                    :disabled="validation.IsValidated">
                                    Validate
                                </button>
                                <span v-else>Validated</span>
                            </td>
                        </template>
                    </List>
                </tab>

                <tab title="Account Data Sources">
                    <h3 class="m-lg-b">Account Data Sources</h3>
                    <AccountDataSources :accountId="props.accountId"></AccountDataSources>
                </tab>

                <tab title="Jobs">
                    <h3 class="m-lg-b">Jobs</h3>
                    <AccountJobs :accountId="props.accountId" />
                </tab>

                <tab title="Delete contacts">
                    <h3 class="m-lg-b">Delete contacts</h3>
                    <h4>Hard delete contacts</h4>

                    <BsAlert :type="AlertTypes.Info" title="Delete is a three step process:" :dismissible="false">
                        <template #content>
                            <ul>
                                <li>Calculate</li>
                                <li>Request delete of contacts</li>
                                <li>Delete each contact</li>
                            </ul>
                            It will start almost immediately, but each contact takes a few seconds to delete so the number of contacts will lower slowly over
                            time.
                        </template>
                    </BsAlert>

                    <div class="form-group">
                        <FormRadioGroup
                            legend="Select contacts to hard delete:"
                            :options="[
                                { label: 'Deleted contacts', value: 'deleted' },
                                { label: 'Contacts without list', value: 'listless' },
                                { label: 'Hard bounced contacts', value: 'bounced' },
                                { label: 'Unsubscribed contacts', value: 'inactive' },
                            ]"
                            :modelValue="state.contactDeleteType"
                            @update:modelValue="onContactDeleteTypeChange"></FormRadioGroup>
                    </div>

                    <div class="form-group">
                        <button @click="countDeletion" class="btn btn-primary m-sm-t" :disabled="state.isLoadingContacts">Calculate</button>
                    </div>

                    <Loading :show="state.isLoadingContacts" loadingStyle="progress-bar" :fixed="true"></Loading>

                    <BsAlert v-if="state.contactDeleteCount != null" :type="AlertTypes.Danger" :dismissible="false">
                        <template #content>
                            <p>
                                <strong>This action can not be undone</strong>
                                and will delete
                                <span>{{ state.contactDeleteCount }}</span>
                                contacts.
                            </p>
                            <div class="form-group">
                                <button class="btn btn-danger btn-sm" v-confirm="{ action: () => deleteContacts() }">Hard Delete</button>
                            </div>
                        </template>
                    </BsAlert>
                </tab>

                <tab title="Disable features">
                    <h3 class="m-lg-b">Disable features</h3>
                    <DisabledFeatures :accountId="props.accountId" />
                </tab>

                <tab title="Delete account">
                    <h3 class="m-lg-b">Delete account</h3>
                    <DeleteAccount :accountId="props.accountId" :isDeleted="state.account.IsDeleted"></DeleteAccount>
                </tab>
            </tabs>
        </div>

        <div v-show="state.currentView === views.companiesList">
            <List
                title="Companies"
                description="Select a company for this account."
                :items="state.companies"
                itemId="ClientCompanyId"
                :filterKeys="['Name']"
                :loading="state.isLoading"
                displayAs="table"
                :tableHeaders="['Name', '']">
                <template #item="{ item }">
                    <td>
                        {{ item.Name }}
                        <span v-show="item.ClientCompanyId === state.account.ClientCompany.ClientCompanyId" class="label label-default m-xxs-l">Current</span>
                    </td>
                    <td class="text-right">
                        <button
                            type="button"
                            class="btn btn-primary btn-sm"
                            @click="updateCurrentCompany(item)"
                            v-show="item.ClientCompanyId !== state.account.ClientCompany.ClientCompanyId">
                            Select
                        </button>
                    </td>
                </template>
            </List>
        </div>
    </template>
</template>

<style scoped>
    .fieldset-grid {
        max-width: 800px;
    }
</style>
